import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Logo } from '@/components/Logo.js';
import { ArrowRight } from '../components/icons.js';

interface FooterLink {
  label: string;
  to: string;
}

const sections: Array<{ heading: string; links: FooterLink[] }> = [
  {
    heading: 'The App',
    links: [
      { label: 'Today', to: '/app/today' },
      { label: 'Prayers', to: '/app/prayers' },
      { label: 'Jesus Prayer', to: '/app/jesus-prayer' },
      { label: 'Fasting', to: '/app/fasting' },
      { label: 'Readings', to: '/app/readings' },
      { label: 'Services', to: '/app/services' },
      { label: 'Calendar', to: '/app/calendar' },
      { label: 'Saints', to: '/app/saints' },
    ],
  },
  {
    heading: 'Learn',
    links: [
      { label: 'Catechism', to: '/learn/catechism' },
      { label: 'What is the Liturgy?', to: '/learn/liturgy' },
      { label: 'On Fasting', to: '/learn/fasting' },
      { label: 'The Saints, Explained', to: '/learn/saints' },
      { label: 'Sacraments', to: '/learn/sacraments' },
      { label: 'Church History', to: '/learn/church-history' },
      { label: 'For Inquirers', to: '/learn/inquirers' },
      { label: 'For Catechumens', to: '/learn/catechumens' },
    ],
  },
  {
    heading: 'Parish & Community',
    links: [
      { label: 'Find a Parish', to: '/parish/find' },
      { label: 'For Parish Admins', to: '/parish/admins' },
      { label: 'Add Your Parish', to: '/parish/add' },
      { label: 'Service Times', to: '/parish/service-times' },
      { label: 'Events', to: '/parish/events' },
      { label: 'Announcements', to: '/parish/announcements' },
    ],
  },
  {
    heading: 'Orthodox Way',
    links: [
      { label: 'About', to: '/about' },
      { label: 'Support the Work', to: '/support' },
      { label: 'Our Sources', to: '/sources' },
      { label: 'Contributors', to: '/contributors' },
      { label: 'Press Kit', to: '/press' },
      { label: 'Brand Kit', to: '/brand' },
      { label: 'Status', to: '/status' },
      { label: 'Changelog', to: '/changelog' },
      { label: 'Roadmap', to: '/roadmap' },
    ],
  },
];

const meta: FooterLink[] = [
  { label: 'Privacy', to: '/privacy' },
  { label: 'Terms', to: '/terms' },
  { label: 'Cookies', to: '/cookies' },
  { label: 'Accessibility', to: '/accessibility' },
  { label: 'Contact', to: '/contact' },
];

export function Footer() {
  const [email, setEmail] = useState('');
  const [submitted, setSubmitted] = useState<'idle' | 'sending' | 'ok' | 'error'>('idle');

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setSubmitted('error');
      return;
    }
    setSubmitted('sending');
    try {
      const res = await fetch('/api/newsletter/subscribe', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email }),
      });
      setSubmitted(res.ok ? 'ok' : 'error');
      if (res.ok) setEmail('');
    } catch {
      setSubmitted('error');
    }
  };

  return (
    <footer className="relative w-full" style={{ background: 'var(--ow-bg-subtle)' }}>
      <div className="mx-auto max-w-container px-5 md:px-8">
        {/* Decorative ornament */}
        <div className="flex items-center justify-center gap-4 pt-16">
          <span className="ow-ornament-line" style={{ background: 'var(--ow-gold)' }} />
          <span className="font-display text-gold" style={{ fontSize: 14, letterSpacing: '0.4em' }}>
            ✦
          </span>
          <span className="ow-ornament-line" style={{ background: 'var(--ow-gold)' }} />
        </div>

        {/* Upper sitemap */}
        <div
          className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-6 gap-10"
          style={{ padding: '64px 0 48px' }}
        >
          <div className="lg:col-span-2">
            <Link to="/" className="flex items-center gap-3 text-gold ow-focusable">
              <Logo size={28} />
              <span
                className="font-display text-primary uppercase"
                style={{ fontSize: 13, fontWeight: 500, letterSpacing: '0.18em' }}
              >
                Orthodox Way
              </span>
            </Link>
            <p
              className="scripture text-secondary mt-4 max-w-[280px]"
              style={{ fontSize: 16, lineHeight: 1.55 }}
            >
              “A small, quiet room you walk into before the world gets loud.”
            </p>

            <form onSubmit={onSubmit} className="mt-8" noValidate>
              <label
                htmlFor="newsletter-email"
                className="block font-display uppercase text-primary"
                style={{ fontSize: 11, letterSpacing: '0.22em', fontWeight: 500 }}
              >
                Receive the weekly Synaxis
              </label>
              <p
                id="newsletter-help"
                className="text-muted mt-1 mb-3"
                style={{ fontSize: 12, lineHeight: 1.5 }}
              >
                Saint of the week + a short reflection. Once a week. No marketing.
              </p>
              <div
                className="flex items-center bg-bg-elevated border border-border rounded-button focus-within:border-gold transition"
                style={{ overflow: 'hidden' }}
              >
                <input
                  id="newsletter-email"
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@example.com"
                  aria-describedby="newsletter-help"
                  className="flex-1 bg-transparent border-0 px-4 py-2.5 text-primary placeholder:text-muted focus:outline-hidden"
                  style={{ fontSize: 14 }}
                />
                <button
                  type="submit"
                  className="ow-focusable px-4 py-2.5 text-gold hover:bg-bg-subtle transition"
                  aria-label="Subscribe"
                  disabled={submitted === 'sending'}
                >
                  <ArrowRight />
                </button>
              </div>
              {submitted === 'ok' && (
                <p className="text-success mt-2" style={{ fontSize: 12 }} role="status">
                  Subscribed. Glory to God.
                </p>
              )}
              {submitted === 'error' && (
                <p className="text-warning mt-2" style={{ fontSize: 12 }} role="alert">
                  Please enter a valid email.
                </p>
              )}
            </form>

            <div className="mt-6 flex items-center gap-3 text-secondary">
              {[
                { name: 'Twitter', href: '#' },
                { name: 'Instagram', href: '#' },
                { name: 'GitHub', href: '#' },
                { name: 'RSS', href: '#' },
              ].map((s) => (
                <a
                  key={s.name}
                  href={s.href}
                  aria-label={s.name}
                  className="ow-focusable w-8 h-8 flex items-center justify-center border border-border rounded-full hover:text-gold hover:border-gold transition"
                  style={{ fontSize: 11 }}
                >
                  {s.name[0]}
                </a>
              ))}
            </div>
          </div>

          {sections.map((s) => (
            <div key={s.heading}>
              <p
                className="font-display uppercase text-primary"
                style={{ fontSize: 11, letterSpacing: '0.22em', fontWeight: 500 }}
              >
                {s.heading}
              </p>
              <ul className="mt-4 space-y-2.5">
                {s.links.map((l) => (
                  <li key={l.to}>
                    <Link
                      to={l.to}
                      className="ow-underline text-secondary"
                      style={{ fontSize: 14 }}
                    >
                      {l.label}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>

        {/* Lower meta row */}
        <div
          className="flex flex-col md:flex-row items-center justify-between gap-3 border-t border-border"
          style={{ padding: '32px 0' }}
        >
          <p className="text-secondary" style={{ fontSize: 13 }}>
            © 2026 Orthodox Way. Glory to God for all things.
          </p>
          <span aria-hidden className="text-gold font-display" style={{ fontSize: 12 }}>
            ✦
          </span>
          <ul className="flex flex-wrap items-center gap-5">
            {meta.map((m) => (
              <li key={m.to}>
                <Link to={m.to} className="ow-underline text-secondary" style={{ fontSize: 13 }}>
                  {m.label}
                </Link>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </footer>
  );
}
